/**
 * Settings, Credentials and coverage (Treatment Plan spec v1.4, S-25).
 *
 * The prescriber's licences, NPI, DEA, agreements and board certification;
 * the signing PIN, passkeys and Prescriber Terms (S-29); and the clinic's
 * malpractice certificate. Each row shows one status: Verified, Expiring in N
 * days, Expired, or Waiting for MCOS check. Signing stops on an expired or
 * unverified licence in the patient's state, so this page is where a blocked
 * sign-off sends the prescriber.
 */

import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { startRegistration } from '@simplewebauthn/browser';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ConnectionsCard } from '../components/Connections';
import { Button, Card, EmptyState, ErrorState, Field, LoadingRows, PageHeader, Pill, inputClass } from '../components/ui';

type Status = 'verified' | 'expiring' | 'expired' | 'waiting';
interface Row { id: string; status: Status; daysLeft: number | null; expires: string | null }
interface CredentialsView {
  prescriber: { name: string; credentials: string; npi: string | null; application: string } | null;
  licenses: Array<Row & { state: string; number: string | null }>;
  credentials: Array<Row & { kind: string; state: string | null; number: string | null; schedules: string[]; hasFile: boolean }>;
  insurance: Array<Row & { carrier: string; policyNumber: string; states: string[]; telehealthCovered: boolean | null; perClaimCents: number | null; aggregateCents: number | null }>;
  signing: {
    pin: { set: boolean; lockedUntil: string | null };
    passkeys: Array<{ id: string; deviceName: string | null; createdAt: string }>;
    terms: { version: string; text: string; approved: boolean; isTest: boolean; acceptedAt: string | null };
  };
  canManageClinic: boolean;
}

const KEY = ['credentials'];
const KIND_LABEL: Record<string, string> = { npi: 'NPI', dea: 'DEA registration', np_pa_agreement: 'NP or PA agreement', board_cert: 'Board certification' };

export function StatusChip({ status, daysLeft }: { status: Status; daysLeft: number | null }) {
  const map: Record<Status, [string, string]> = {
    verified: ['Verified', 'bg-mint text-forest'],
    expiring: [`Expiring in ${daysLeft ?? 0} days`, 'bg-risk-yellow-soft text-risk-yellow'],
    expired: ['Expired', 'bg-alert-soft text-alert'],
    waiting: ['Waiting for MCOS check', 'bg-[#e6eef7] text-navy'],
  };
  const [text, tone] = map[status];
  return <Pill className={tone}>{text}</Pill>;
}

async function uploadDocument(file: File): Promise<string> {
  const form = new FormData();
  form.append('file', file);
  return (await api.post<{ fileId: string }>('/credentials/files', form as unknown as FormData)).fileId;
}

export function CredentialsPage() {
  const view = useQuery({ queryKey: KEY, queryFn: () => api.get<CredentialsView>('/credentials') });
  return (
    <div className="mx-auto max-w-[980px]">
      <PageHeader
        title="Credentials and coverage"
        subtitle="What lets you sign plans, and what MCOS has checked."
        actions={<Link to="/settings/audit-trail" className="text-[14px] font-semibold text-forest underline">Legal audit trail</Link>}
      />
      {view.isLoading && <LoadingRows rows={5} />}
      {view.error && <ErrorState error={view.error} retry={() => void view.refetch()} />}
      {view.data && (
        <div className="space-y-5">
          <Signing data={view.data} />
          <Licences data={view.data} />
          <OtherCredentials data={view.data} />
          <Malpractice data={view.data} />
          {view.data.canManageClinic && <ClinicConsent />}
          <ConnectionsCard path="/credentials/connections" />
        </div>
      )}
    </div>
  );
}

function useRefresh() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: KEY });
    void qc.invalidateQueries({ queryKey: ['signoff-me'] });
    void qc.invalidateQueries({ queryKey: ['signoff'] });
    void qc.invalidateQueries({ queryKey: ['signoff-preview'] });
  };
}

function Failure({ error }: { error: unknown }) {
  if (!error) return null;
  return <p className="mt-2 text-sm text-red-600" role="alert">{error instanceof Error ? error.message : 'That did not save.'}</p>;
}

function Line({ title, detail, chip, children }: { title: string; detail?: string; chip?: ReactNode; children?: ReactNode }) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 py-2.5">
      <div className="min-w-0">
        <p className="text-[14px] font-medium text-navy">{title}</p>
        {detail && <p className="text-[12.5px] text-[#5b6b7c]">{detail}</p>}
      </div>
      <div className="flex items-center gap-2">
        {chip}
        {children}
      </div>
    </li>
  );
}

function Signing({ data }: { data: CredentialsView }) {
  const refresh = useRefresh();
  const [pin, setPin] = useState('');
  const [again, setAgain] = useState('');
  const [showTerms, setShowTerms] = useState(false);
  const savePin = useMutation({ mutationFn: () => api.put('/signoff/me/pin', { pin }), onSuccess: () => { setPin(''); setAgain(''); refresh(); } });
  const accept = useMutation({ mutationFn: () => api.post('/signoff/me/terms', { version: data.signing.terms.version }), onSuccess: refresh });
  const addPasskey = useMutation({
    mutationFn: async () => {
      const options = await api.post<Parameters<typeof startRegistration>[0]['optionsJSON']>('/signoff/me/passkeys/options');
      const response = await startRegistration({ optionsJSON: options });
      return api.post('/signoff/me/passkeys', { response, deviceName: navigator.platform || null });
    },
    onSuccess: refresh,
  });
  const removePasskey = useMutation({ mutationFn: (id: string) => api.delete(`/signoff/me/passkeys/${id}`), onSuccess: refresh });
  const { terms } = data.signing;

  return (
    <Card title="Signing" subtitle="Sign each plan with a passkey (Face ID, Touch ID) or your 6-digit signing PIN.">
      <ul className="divide-y divide-hairline">
        <Line
          title="Prescriber Terms"
          detail={terms.acceptedAt ? `You accepted ${terms.version} on ${terms.acceptedAt.slice(0, 10)}.` : terms.approved ? 'Read and accept once before your first signature. About 2 minutes.' : 'Waiting for MCOS to approve the terms.'}
          chip={terms.acceptedAt ? <Pill className="bg-mint text-forest">Accepted</Pill> : <Pill className="bg-risk-yellow-soft text-risk-yellow">Not accepted</Pill>}
        >
          {terms.approved && (
            <Button size="sm" variant="secondary" onClick={() => setShowTerms(!showTerms)}>
              {showTerms ? 'Hide' : 'Read'}
            </Button>
          )}
        </Line>
        {showTerms && (
          <li className="py-2.5" data-testid="terms-text">
            {terms.isTest && <p className="mb-2 rounded bg-[#fff4e5] px-2 py-1 text-[12px] text-[#7a4b12]">Test wording for local use. The live site shows the text counsel approved.</p>}
            <div className="max-h-[240px] overflow-y-auto whitespace-pre-line rounded bg-[#f5f8fb] p-3 text-[12.5px] leading-relaxed">{terms.text}</div>
            {!terms.acceptedAt && (
              <div className="mt-2">
                <Button size="sm" loading={accept.isPending} onClick={() => accept.mutate()}>I accept the Prescriber Terms</Button>
              </div>
            )}
          </li>
        )}
        <Line title="Signing PIN" detail={data.signing.pin.lockedUntil ? `Locked after wrong PINs until ${new Date(data.signing.pin.lockedUntil).toLocaleTimeString()}.` : data.signing.pin.set ? 'Set. Enter a new one below to change it.' : 'Not set.'} chip={data.signing.pin.set ? <Pill className="bg-mint text-forest">Set</Pill> : <Pill className="bg-risk-yellow-soft text-risk-yellow">Not set</Pill>} />
        <li className="py-2.5">
          <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
            <Field label="New 6-digit PIN"><input id="new-pin" type="password" inputMode="numeric" autoComplete="new-password" maxLength={6} className={inputClass} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} /></Field>
            <Field label="Repeat it" error={again.length === 6 && again !== pin ? 'The two PINs differ' : undefined}><input id="repeat-pin" type="password" inputMode="numeric" autoComplete="new-password" maxLength={6} className={inputClass} value={again} onChange={(e) => setAgain(e.target.value.replace(/\D/g, ''))} /></Field>
            <Button disabled={pin.length !== 6 || pin !== again} loading={savePin.isPending} onClick={() => savePin.mutate()}>{data.signing.pin.set ? 'Change PIN' : 'Set PIN'}</Button>
          </div>
          <Failure error={savePin.error} />
        </li>
        {data.signing.passkeys.map((key) => (
          <Line key={key.id} title={`Passkey${key.deviceName ? `, ${key.deviceName}` : ''}`} detail={`Added ${key.createdAt.slice(0, 10)}`}>
            <Button size="sm" variant="secondary" loading={removePasskey.isPending} onClick={() => removePasskey.mutate(key.id)}>Remove</Button>
          </Line>
        ))}
        <li className="py-2.5">
          <Button variant="secondary" loading={addPasskey.isPending} onClick={() => addPasskey.mutate()}>Add a passkey on this device</Button>
          <Failure error={addPasskey.error ?? removePasskey.error ?? accept.error} />
        </li>
      </ul>
    </Card>
  );
}

function Licences({ data }: { data: CredentialsView }) {
  // Clinic prescribers keep their licences on their provider profile; MCOS loads the medical director's.
  const { can } = useAuth();
  const edit = can('ADMIN', 'CLINICIAN') ? <Link to="/application" className="text-[13px] font-semibold text-forest underline">Edit licences</Link> : undefined;
  return (
    <Card title="State licences" subtitle="You can sign only for patients in a state where your licence is verified and current." action={edit}>
      {data.licenses.length === 0 ? (
        <EmptyState title="No licences on file" body="Add your state licences on your provider profile. MCOS verifies each one." />
      ) : (
        <ul className="divide-y divide-hairline">
          {data.licenses.map((l) => (
            <Line key={l.id} title={`${l.state} medical licence`} detail={[l.number, l.expires ? `expires ${l.expires}` : null].filter(Boolean).join(', ')} chip={<StatusChip status={l.status} daysLeft={l.daysLeft} />} />
          ))}
        </ul>
      )}
    </Card>
  );
}

function OtherCredentials({ data }: { data: CredentialsView }) {
  const refresh = useRefresh();
  const [form, setForm] = useState({ kind: 'dea', state: '', number: '', schedules: '', expiresOn: '' });
  const [file, setFile] = useState<File | null>(null);
  const add = useMutation({
    mutationFn: async () => {
      const fileId = file ? await uploadDocument(file) : null;
      return api.post('/credentials/prescriber', {
        kind: form.kind,
        state: form.state || null,
        number: form.number || null,
        schedules: form.schedules.split(/[,\s]+/).filter(Boolean),
        expiresOn: form.expiresOn || null,
        fileId,
      });
    },
    onSuccess: () => {
      setForm({ kind: 'dea', state: '', number: '', schedules: '', expiresOn: '' });
      setFile(null);
      refresh();
    },
  });
  const needsState = form.kind === 'dea' || form.kind === 'np_pa_agreement';
  return (
    <Card title="Other credentials" subtitle="NPI, DEA per state for controlled items, NP or PA agreements, board certification.">
      {data.credentials.length > 0 && (
        <ul className="divide-y divide-hairline">
          {data.credentials.map((c) => (
            <Line
              key={c.id}
              title={`${KIND_LABEL[c.kind] ?? c.kind}${c.state ? `, ${c.state}` : ''}`}
              detail={[c.number, c.schedules.length ? `schedules ${c.schedules.join(', ')}` : null, c.expires ? `expires ${c.expires}` : null, c.hasFile ? 'document on file' : null].filter(Boolean).join(', ')}
              chip={<StatusChip status={c.status} daysLeft={c.daysLeft} />}
            />
          ))}
        </ul>
      )}
      <div className="mt-3 grid gap-3 sm:grid-cols-3" data-testid="credential-form">
        <Field label="Kind">
          <select className={inputClass} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
            {Object.entries(KIND_LABEL).map(([value, text]) => <option key={value} value={value}>{text}</option>)}
          </select>
        </Field>
        <Field label={needsState ? 'State' : 'State, if any'}><input className={inputClass} maxLength={2} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} /></Field>
        <Field label="Number"><input className={inputClass} value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} /></Field>
        {form.kind === 'dea' && <Field label="Schedules" hint="For example: III, IV"><input className={inputClass} value={form.schedules} onChange={(e) => setForm({ ...form, schedules: e.target.value })} /></Field>}
        <Field label="Expires on"><input type="date" className={inputClass} value={form.expiresOn} onChange={(e) => setForm({ ...form, expiresOn: e.target.value })} /></Field>
        <Field label={form.kind === 'np_pa_agreement' ? 'Signed agreement' : 'Document, optional'}><input type="file" accept="application/pdf,image/png,image/jpeg" className="text-[13px]" onChange={(e) => setFile(e.target.files?.[0] ?? null)} /></Field>
      </div>
      <div className="mt-3">
        <Button disabled={(needsState && form.state.length !== 2) || (form.kind === 'np_pa_agreement' && !file)} loading={add.isPending} onClick={() => add.mutate()}>Add credential</Button>
      </div>
      <Failure error={add.error} />
    </Card>
  );
}

function Malpractice({ data }: { data: CredentialsView }) {
  const refresh = useRefresh();
  const [form, setForm] = useState({ carrier: '', policyNumber: '', perClaim: '', aggregate: '', states: '', telehealth: true, expiresOn: '' });
  const [file, setFile] = useState<File | null>(null);
  const add = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Attach the certificate.');
      const fileId = await uploadDocument(file);
      return api.post('/credentials/insurance', {
        carrier: form.carrier,
        policyNumber: form.policyNumber,
        perClaimDollars: form.perClaim ? Number(form.perClaim) : null,
        aggregateDollars: form.aggregate ? Number(form.aggregate) : null,
        states: form.states.split(/[,\s]+/).filter(Boolean).map((s) => s.toUpperCase()),
        telehealthCovered: form.telehealth,
        expiresOn: form.expiresOn,
        fileId,
      });
    },
    onSuccess: () => {
      setForm({ carrier: '', policyNumber: '', perClaim: '', aggregate: '', states: '', telehealth: true, expiresOn: '' });
      setFile(null);
      refresh();
    },
  });
  const money = (cents: number | null) => (cents == null ? null : `$${(cents / 100).toLocaleString('en-US')}`);
  return (
    <Card title="Malpractice certificate" subtitle="Your clinic's certificate must be current, list the patient's state and cover telehealth.">
      {data.insurance.length === 0 ? (
        <EmptyState title="No certificate on file" body={data.canManageClinic ? 'Upload it below. MCOS checks it before plans can be signed.' : 'Your clinic admin uploads it.'} />
      ) : (
        <ul className="divide-y divide-hairline">
          {data.insurance.map((i) => (
            <Line
              key={i.id}
              title={`${i.carrier}, policy ${i.policyNumber}`}
              detail={[i.states.join(', '), i.telehealthCovered ? 'covers telehealth' : 'telehealth not covered', money(i.perClaimCents) && `${money(i.perClaimCents)} per claim`, i.expires && `expires ${i.expires}`].filter(Boolean).join(', ')}
              chip={<StatusChip status={i.status} daysLeft={i.daysLeft} />}
            />
          ))}
        </ul>
      )}
      {data.canManageClinic && (
        <>
          <div className="mt-3 grid gap-3 sm:grid-cols-3" data-testid="insurance-form">
            <Field label="Carrier"><input className={inputClass} value={form.carrier} onChange={(e) => setForm({ ...form, carrier: e.target.value })} /></Field>
            <Field label="Policy number"><input className={inputClass} value={form.policyNumber} onChange={(e) => setForm({ ...form, policyNumber: e.target.value })} /></Field>
            <Field label="States covered" hint="For example: TX, FL"><input className={inputClass} value={form.states} onChange={(e) => setForm({ ...form, states: e.target.value })} /></Field>
            <Field label="Per claim, dollars"><input className={inputClass} inputMode="numeric" value={form.perClaim} onChange={(e) => setForm({ ...form, perClaim: e.target.value.replace(/\D/g, '') })} /></Field>
            <Field label="Aggregate, dollars"><input className={inputClass} inputMode="numeric" value={form.aggregate} onChange={(e) => setForm({ ...form, aggregate: e.target.value.replace(/\D/g, '') })} /></Field>
            <Field label="Expires on"><input type="date" className={inputClass} value={form.expiresOn} onChange={(e) => setForm({ ...form, expiresOn: e.target.value })} /></Field>
            <Field label="Certificate"><input type="file" accept="application/pdf,image/png,image/jpeg" className="text-[13px]" onChange={(e) => setFile(e.target.files?.[0] ?? null)} /></Field>
          </div>
          <label className="mt-3 flex items-center gap-2 text-[13.5px]"><input type="checkbox" checked={form.telehealth} onChange={(e) => setForm({ ...form, telehealth: e.target.checked })} />Covers telehealth</label>
          <div className="mt-3">
            <Button disabled={!form.carrier || !form.policyNumber || !form.states.trim() || !form.expiresOn || !file} loading={add.isPending} onClick={() => add.mutate()}>Upload certificate</Button>
          </div>
          <Failure error={add.error} />
        </>
      )}
    </Card>
  );
}

interface ConsentStatus {
  patientsSee: { version: string; clinicOwn: boolean; isTest: boolean } | null;
  versions: Array<{ version: string; status: string; approvedOn: string | null; materialChange: boolean }>;
}

/** CN-003: the clinic's own counsel-approved consent, under MCOS's key points. MCOS approves it before patients see it. */
function ClinicConsent() {
  const qc = useQueryClient();
  const status = useQuery({ queryKey: ['clinic-consent'], queryFn: () => api.get<ConsentStatus>('/credentials/consent') });
  const [form, setForm] = useState({ fullText: '', counselName: '', counselApprovedOn: '', materialChange: false });
  const submit = useMutation({
    mutationFn: () => api.post<ConsentStatus>('/credentials/consent', form),
    onSuccess: (fresh) => {
      qc.setQueryData(['clinic-consent'], fresh);
      setForm({ fullText: '', counselName: '', counselApprovedOn: '', materialChange: false });
    },
  });
  const sees = status.data?.patientsSee;
  return (
    <Card title="Patient consent" subtitle="Patients see MCOS's key points on the Accept screen. 'Read the full consent' can open your clinic's own text, once your counsel and MCOS have approved it.">
      {sees && (
        <p className="text-[13.5px] text-navy" data-testid="consent-in-use">
          Patients see {sees.clinicOwn ? 'your clinic\'s consent' : 'the MCOS default consent'} ({sees.version}{sees.isTest ? ', test wording' : ''}).
        </p>
      )}
      {status.data && status.data.versions.length > 0 && (
        <ul className="mt-2 divide-y divide-hairline">
          {status.data.versions.map((v) => (
            <Line key={v.version} title={v.version} detail={v.approvedOn ? `Approved by MCOS on ${v.approvedOn}` : 'Waiting for MCOS approval'} chip={v.status === 'approved' ? <Pill className="bg-mint text-forest">approved</Pill> : <Pill className="bg-risk-yellow-soft text-risk-yellow">{v.status}</Pill>} />
          ))}
        </ul>
      )}
      <div className="mt-3 space-y-3" data-testid="consent-form">
        <Field label="Full consent text" hint="Paste the wording your counsel approved.">
          <textarea className={`${inputClass} min-h-[140px]`} value={form.fullText} onChange={(e) => setForm({ ...form, fullText: e.target.value })} />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Approved by counsel"><input className={inputClass} value={form.counselName} onChange={(e) => setForm({ ...form, counselName: e.target.value })} /></Field>
          <Field label="Approved on"><input type="date" className={inputClass} value={form.counselApprovedOn} onChange={(e) => setForm({ ...form, counselApprovedOn: e.target.value })} /></Field>
        </div>
        <label className="flex items-center gap-2 text-[13.5px]"><input type="checkbox" checked={form.materialChange} onChange={(e) => setForm({ ...form, materialChange: e.target.checked })} />Counsel says this is a material change (patients on an active plan re-accept)</label>
        <Button disabled={form.fullText.trim().length < 200 || form.counselName.trim().length < 2 || !form.counselApprovedOn} loading={submit.isPending} onClick={() => submit.mutate()}>Submit for MCOS approval</Button>
        <Failure error={submit.error} />
      </div>
    </Card>
  );
}
